import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import FractionInput, { formatFractionLatexCore } from "../../../../components/inputs/FractionInput";

/** ===== UŁAMKI ===== */
function gcd(a, b) { a = Math.abs(a); b = Math.abs(b); while (b) [a, b] = [b, a % b]; return a || 1; }
function normFrac(fr) {
  if (!fr || fr.denominator == null || fr.numerator == null || fr.denominator === 0) {
    return { numerator: NaN, denominator: NaN };
  }
  const sign = fr.denominator < 0 ? -1 : 1;
  const n = fr.numerator * sign;
  const d = Math.abs(fr.denominator);
  const g = gcd(n, d);
  return { numerator: n / g, denominator: d / g };
}
function equalsFrac(a, b) { const A = normFrac(a), B = normFrac(b); return A.numerator === B.numerator && A.denominator === B.denominator; }

function toLatex(fr) {
  const f = normFrac(fr);
  if (Number.isNaN(f.numerator) || Number.isNaN(f.denominator)) return "\\text{?}";
  const sign = f.numerator < 0 ? "-" : "";
  const num = Math.abs(f.numerator);
  const den = f.denominator;
  return den === 1 ? `${sign}${num}` : `${sign}\\dfrac{${num}}{${den}}`;
}

/** ===== ŁADNE DANE ===== */
const TRIPLES = [
  [3, 4, 5],
  [5, 12, 13],
  [8, 15, 17],
  [7, 24, 25],
  [20, 21, 29],
  [12, 35, 37],
];

export const type = "GivenCosFindSinTanTask";

/**
 * variant: "I" | "II"
 *  I   ćwiartka: sin > 0, cos > 0, tan > 0
 *  II  ćwiartka: sin > 0, cos < 0, tan < 0
 */
export function generateTask({ variant = "I" } = {}) {
  const [a, b, c] = TRIPLES[Math.floor(Math.random() * TRIPLES.length)];
  // Kąt przy boku 'a' lub 'b'
  const angleAt = Math.random() < 0.5 ? "a" : "b";

  // Dla kąta przy 'a': sin = a/c, cos = b/c, tan = a/b
  // Dla kąta przy 'b': sin = b/c, cos = a/c, tan = b/a
  let sinPos, cosPos, tanPos;
  if (angleAt === "a") {
    sinPos = { numerator: a, denominator: c };
    cosPos = { numerator: b, denominator: c };
    tanPos = { numerator: a, denominator: b };
  } else {
    sinPos = { numerator: b, denominator: c };
    cosPos = { numerator: a, denominator: c };
    tanPos = { numerator: b, denominator: a };
  }

  // Znak wg ćwiartki
  const sin = normFrac({ numerator: +sinPos.numerator, denominator: sinPos.denominator }); // I i II: dodatni
  const cos = normFrac({ numerator: (variant === "I" ? +cosPos.numerator : -cosPos.numerator), denominator: cosPos.denominator });
  const tan = normFrac({ numerator: (variant === "I" ? +tanPos.numerator : -tanPos.numerator), denominator: tanPos.denominator });

  return {
    type,
    data: { variant, a, b, c, angleAt, given: { cos }, expected: { sin, tan } }
  };
}

export function renderTask({ data }) {
  const { variant, given: { cos } } = data;
  const range = variant === "I" ? "(0^\\circ, 90^\\circ)" : "(90^\\circ, 180^\\circ)";
  return (
    <div style={{ marginBottom: "0.5rem" }}>
      Dany <InlineMath math={'\\cos\\alpha'} /> = <InlineMath math={toLatex(cos)} />, oblicz{" "}
      <InlineMath math={'\\sin\\alpha'} /> oraz <InlineMath math={'\\tan\\alpha'} />.
      <div style={{ marginTop: "0.5rem" }}>
        Zakres kąta: <InlineMath math={range} />
      </div>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  const ans = userAnswer ?? {};
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <InlineMath math={'\\sin\\alpha ='} />
        <FractionInput
          value={ans.sin}
          onChange={(v) => onChange({ ...ans, sin: v })}
          disabled={disabled}
          label={'\\sin\\alpha'}
        />
      </div>
      <div className="flex items-center gap-2">
        <InlineMath math={'\\tan\\alpha ='} />
        <FractionInput
          value={ans.tan}
          onChange={(v) => onChange({ ...ans, tan: v })}
          disabled={disabled}
          label={'\\tan\\alpha'}
        />
      </div>
    </div>
  );
}

/** ===== Parsowanie odpowiedzi i sprawdzanie ===== */
export function checkAnswer({ data }, userAnswer) {
  const exp = data.expected;

  const isBlank = (v) => {
    if (v == null) return true;
    if (typeof v === "object") {
      if (v.numerator != null && v.denominator != null) return false;
      const raw = v.raw ?? v.value ?? null;
      const s = String(raw ?? "").trim().toLowerCase();
      return s === "" || s === "null" || s === "undefined" || s === "-" || s === "+";
    }
    if (typeof v === "string") {
      const s = v.trim().toLowerCase();
      return s === "" || s === "null" || s === "undefined";
    }
    if (typeof v === "number") return Number.isNaN(v);
    return false;
  };

  if (!userAnswer || isBlank(userAnswer.sin) || isBlank(userAnswer.tan)) {
    return {
      correct: false,
      message: "Uzupełnij obie wartości.",
      score: 0,
      maxScore: 2,
      parts: { "sinα": false, "tanα": false },
      isCorrect: false,
    };
  }

  const TOL = 0.01;

  const toNumber = (f) => {
    if (!f || f.numerator == null || f.denominator == null || f.denominator === 0) return NaN;
    return f.numerator / f.denominator;
  };

  const parseDecimal = (raw) => {
    if (raw == null) return null;
    let s = String(raw).trim().replace(/\u2212/g, "-").replace(",", ".");
    if (/^\(.*\)$/.test(s)) s = s.slice(1, -1).trim();
    if (/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/.test(s)) return parseFloat(s);
    return null;
  };

  const parseFraction = (val) => {
    if (val && typeof val === "object" && val.numerator != null && val.denominator != null) return val;

    const raw = typeof val === "object" ? val.raw : val;
    if (raw == null) return val;

    const s = String(raw).trim().replace(/\u2212/g, "-");

    // mieszany: "1 2/3"
    let m = s.match(/^([+-])?\s*(\d+)\s+(\d+)\s*\/\s*([+-]?\d+)$/);
    if (m) {
      const lead = m[1] === "-" ? -1 : 1;
      const I = parseInt(m[2], 10);
      const N = parseInt(m[3], 10);
      const D = parseInt(m[4], 10);
      const num = lead * (I * Math.abs(D) + N) * (D < 0 ? -1 : 1);
      const den = Math.abs(D);
      return { numerator: num, denominator: den };
    }

    // zwykły ułamek: "-3/4", "(3)/(-4)"
    m = s.match(/^([+-])?\s*\(?\s*([+-]?\d+)\s*\)?\s*\/\s*([+-]?\d+)$/);
    if (m) {
      const lead = m[1] === "-" ? -1 : 1;
      const N = parseInt(m[2], 10);
      const D = parseInt(m[3], 10);
      return { numerator: lead * N, denominator: D };
    }

    // całkowita
    m = s.match(/^([+-]?\d+)$/);
    if (m) return { numerator: parseInt(m[1], 10), denominator: 1 };

    return val;
  };

  const isCorrectOne = (userVal, expectedFrac) => {
    const frac = parseFraction(userVal);
    if (frac && typeof frac === "object" && frac.numerator != null && frac.denominator != null) {
      return equalsFrac(frac, expectedFrac); // używa Twojej funkcji equalsFrac z pliku
    }
    const raw = typeof userVal === "object" ? userVal.raw : userVal;
    const num = parseDecimal(raw);
    if (num == null) return false;

    const expectedNum = toNumber(expectedFrac);
    return Number.isFinite(expectedNum) && Math.abs(num - expectedNum) <= TOL;
  };

  const okSin = isCorrectOne(userAnswer.sin, exp.sin);
  const okTan = isCorrectOne(userAnswer.tan, exp.tan);

  const score = (okSin ? 1 : 0) + (okTan ? 1 : 0);
  const maxScore = 2;
  const parts = { "sinα": okSin, "tanα": okTan };

  let message = "Świetnie!";
  if (score !== 2) {
    if (score === 0) message = "Błędne obie wartości. Sprawdź znak i skrócenie.";
    if (score === 1 && !okSin) message = "Dobrze liczysz \\(\\tan\\alpha\\), ale sprawdź \\(\\sin\\alpha\\) (znak/skrócenie).";
    if (score === 1 && !okTan) message = "Dobrze liczysz \\(\\sin\\alpha\\), ale sprawdź \\(\\tan\\alpha\\) (znak/skrócenie).";
  }

  return {
    correct: score === 2,
    message,
    score,
    maxScore,
    parts,
    isCorrect: score === 2,
  };
}


export function renderAnswer({ data }) {
  const { expected: { sin, tan } } = data;
  return (
    <div style={{ textAlign: "left", marginTop: "0.5rem" }}>
      <div style={{ marginBottom: "1rem" }}>
        <InlineMath math={`\\sin\\alpha = ${toLatex(sin)}`} />
      </div>
      <div>
        <InlineMath math={`\\tan\\alpha = ${toLatex(tan)}`} />
      </div>
    </div>
  );
}

export function renderExplanation({ data }) {
  const {
    variant,
    a, b, c,
    angleAt,
    given: { cos },
    expected: { sin, tan }
  } = data;

  // „Pozytywne” długości z trójkąta
  const cosNum = angleAt === "a" ? b : a; // licznik w cos (dodatni w długościach)
  const sinNum = angleAt === "a" ? a : b; // licznik w sin
  const wrap = (x) => `{${x}}`;

  return (
    <div style={{ textAlign: "left", marginTop: "0.5rem", lineHeight: 1.8 }}>
      <div style={{ marginBottom: "0.5rem" }}>
        <strong>Krok 1:</strong> Jedynka trygonometryczna
      </div>

      <BlockMath math={`\\sin^2\\alpha + \\cos^2\\alpha = 1`} />
      <BlockMath math={`\\sin^2\\alpha = 1 - \\cos^2\\alpha`} />
      <BlockMath math={`\\sin^2\\alpha = 1 - \\left(${wrap(toLatex(cos))}\\right)^2`} />
      <BlockMath math={`\\sin^2\\alpha = 1 - \\dfrac{${cosNum * cosNum}}{${c * c}}`} />
      <BlockMath math={`\\sin^2\\alpha = \\dfrac{${sinNum * sinNum}}{${c * c}}`} />
      <BlockMath math={`|\\sin\\alpha| = \\dfrac{${sinNum}}{${c}}`} />

      <div style={{ marginBottom: "1rem" }}>
        W ćwiartce {variant} znak daje:
        <BlockMath math={`\\sin\\alpha = ${toLatex(sin)}`} />
      </div>

      <div style={{ marginBottom: "0.5rem" }}>
        <strong>Krok 2:</strong> Definicja tangensa
      </div>
      <BlockMath
        math={`\\tan\\alpha = \\dfrac{\\sin\\alpha}{\\cos\\alpha} = {\\small \\dfrac{${toLatex(sin)}}{${toLatex(cos)}}} = ${toLatex(tan)}`}
      />
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer) return <i>(brak odpowiedzi)</i>;

  const isBlankInput = (v) => {
    if (v == null) return true;
    if (typeof v === "object") {
      if (v.numerator != null && v.denominator != null) return false;
      const raw = v.raw ?? v.value ?? null;
      const s = String(raw ?? "").trim().toLowerCase();
      return s === "" || s === "null" || s === "undefined" || s === "-" || s === "+";
    }
    if (typeof v === "string") {
      const s = v.trim().toLowerCase();
      return s === "" || s === "null" || s === "undefined";
    }
    if (typeof v === "number") return Number.isNaN(v);
    return false;
  };

  const sanitizeRaw = (raw) => {
    const s = String(raw ?? "").trim();
    const low = s.toLowerCase();
    if (low === "null" || low === "undefined") return "";
    return s;
  };

  const sinBlank = isBlankInput(userAnswer.sin);
  const tanBlank = isBlankInput(userAnswer.tan);

  if (sinBlank && tanBlank) return <i>(brak odpowiedzi)</i>;

  const sinRaw = typeof userAnswer.sin === "object" ? userAnswer.sin.raw : userAnswer.sin;
  const tanRaw = typeof userAnswer.tan === "object" ? userAnswer.tan.raw : userAnswer.tan;

  return (
    <div style={{ textAlign: "left", marginTop: "0.5rem" }}>
      <div style={{ marginBottom: "1rem" }}>
        <InlineMath math={`\\displaystyle \\sin\\alpha = ${formatFractionLatexCore(sanitizeRaw(sinRaw))}`} />
      </div>
      <div>
        <InlineMath math={`\\displaystyle \\tan\\alpha = ${formatFractionLatexCore(sanitizeRaw(tanRaw))}`} />
      </div>
    </div>
  );
}
